/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type Host, type OnInit, Component, Injector, Input } from '@angular/core';
import { type ColumnDef, type TableConfig, I18nService, TableAbstract, UNPLACEHOLDER } from '@dm/core';
import { eventType, infigo } from '@dm/live-material-infigo';
import { type Host as HostType } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { HostsStrService } from 'app/feature/service/block-service/hosts/hosts-str.service';
import { type SearchObj } from 'app/feature/share/global-search/global-search-str.service';

import { InfigoService } from '../../../infigo.service';

@Component({
    selector: 'app-member-host-table',
    templateUrl: './member-host-table.component.html',
    styleUrls: ['../../resource.scss'],
})
export class MemberHostTableComponent extends TableAbstract<Host> implements OnInit {
    @Input() data: HostType[];
    @Input() loading;
    // 表格行配置对象
    tableColumnDefs: ColumnDef[];
    // 表格总配置对象
    tableConfig: TableConfig;
    jumpPath: SearchObj;
    usageValue = this.infigoService.usageValue;
    SWITCH = SWITCH;
    FeatureConfig = FeatureConfig;

    constructor(private i18n: I18nService, public str: HostsStrService, private infigoService: InfigoService, injector: Injector) {
        super(injector);
    }

    ngOnInit(): void {
        this.initTable();
    }

    initBtns(): void {
    }

    initTable(): void {
        this.tableColumnDefs = [
            {
                field: 'NAME',
                header: this.i18n.get('common.name_column'),
                filterType: 'search',
                sortable: true,
                onClick: (data: any) => this.setRouterPath(data),
            },
            {
                field: 'HEALTHSTATUS',
                header: this.i18n.get('common.healthStatus_column'),
                render: (value: string) => this.str.healthStatusObj[value] || UNPLACEHOLDER,
            },
            {
                field: 'IP',
                header: this.i18n.get('common.IPaddress_column'),
                render: (value) => value || UNPLACEHOLDER,
            },
            {
                field: 'usage',
                header: SWITCH.IS_DIRECT_MODE ?
                    this.i18n.get('common.capacity_column') :
                    this.i18n.get('host.host_view_summary_lunCapUsage_label'),
                width: 180,
                align: 'right',
                hideTextTips: true,
            },
            {
                field: 'OPERATIONSYSTEM',
                header: this.i18n.get('common.operatingSystem_lab'),
                filterType: 'dropdown',
                options: this.str.osOptions,
                render: (value: string): string => this.str.osOBj[Number(value)],
                /**
                 * 由于DM界面将CLI创建的操作系统枚举值为9以及10的主机类型显示为枚举值为1以及12的类型，
                 * 故应当在过滤操作系统类型为1的主机同时过滤出类型为9的，过滤类型为12的主机同时过滤出类型为10的
                 */
                filterFn: (condition: string, rowData: HostType): boolean => {
                    if (condition === this.str.osObjFilterParams.OS_TYPE_E_WINDOWS.displayValue) { // Window
                        return [condition, this.str.osObjFilterParams.OS_TYPE_E_WINDOWS.hiddenValue].includes(rowData.OPERATIONSYSTEM);
                    }

                    if (condition === this.str.osObjFilterParams.OS_TYPE_E_ORACLE_VM_SEVER_FOR_X86.displayValue) { // Oracle_VM_Server_for_x86
                        return [condition, this.str.osObjFilterParams.OS_TYPE_E_ORACLE_VM_SEVER_FOR_X86.hiddenValue].includes(rowData.OPERATIONSYSTEM);
                    }

                    return rowData.OPERATIONSYSTEM === condition;
                },
            },
            {
                field: 'INITIATORNUM',
                header: this.i18n.get('host.host_term_initiatorQuantity_lab'),
                clickable: (data, col: ColumnDef) => data[col.field] && data[col.field] !== '0',
            },
        ];

        this.tableConfig = {
            dataKey: 'ID',
            motion: false,
            rows: 5,
        };
    }

    setRouterPath(value: { ID: any; NAME: any }): void {
        this.jumpPath = {
            id: value.ID,
            name: value.NAME,
            precisionType: 'host',
            COMBINATION: '',
        };
        const routerEvent = this.infigoService.transformSearchParams(this.jumpPath);
        infigo.dispatch(eventType.CLICK_LIST_ITEM, routerEvent);
    }
}
